import { marked } from 'marked';
import type { Lang } from '@/i18n';
import { DEFAULT_LANG, isLang } from '@/i18n';

/**
 * Les articles sont des fichiers Markdown versionnés dans `src/content/blog/`.
 *
 * `import.meta.glob(..., { eager: true, as: 'raw' })` est une transformation
 * STATIQUE : Vite inline le contenu des fichiers dans le bundle au moment du
 * build. Pas de `fs` au runtime, donc pas de dossier de contenu à embarquer
 * dans l'image Docker, et aucun import dynamique (interdit dans ce dépôt).
 *
 * Conséquence à connaître : publier un article demande un build. C'est voulu —
 * un article est du code relu en PR, pas une entrée de CMS.
 *
 * `.mdx` est accepté parce que DispatchSEO écrit ses guides dans ce format. Il
 * est rendu comme du Markdown : pas de JSX (le passe-droit de la review refuse
 * de toute façon toute balise dans un article).
 */
const RAW_ARTICLES = import.meta.glob(['../content/blog/*.md', '../content/blog/*.mdx'], {
  eager: true,
  as: 'raw',
}) as Record<string, string>;

export interface Article {
  slug: string;
  title: string;
  description: string;
  /** ISO `YYYY-MM-DD`. */
  date: string;
  lang: Lang;
  author: string;
  tags: string[];
  /**
   * Chemin servi de la couverture (`/blog/covers/<slug>.webp`), ou `undefined`
   * si l'article n'en a pas : la page et la carte s'affichent alors sans image.
   */
  cover?: string;
  /** Markdown brut, sans l'en-tête. */
  markdown: string;
  readingMinutes: number;
}

/**
 * Lit l'en-tête `---` d'un fichier Markdown.
 *
 * Un parseur YAML complet (gray-matter) serait de l'artillerie pour des
 * en-têtes qu'on écrit nous-mêmes : on accepte `clé: valeur`, les guillemets
 * optionnels et les listes séparées par des virgules. Toute autre forme est
 * ignorée plutôt que devinée.
 */
function parseFrontmatter(raw: string): { data: Record<string, string>; body: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(raw);
  if (!match) return { data: {}, body: raw };

  const data: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const sep = line.indexOf(':');
    if (sep <= 0) continue;
    const key = line.slice(0, sep).trim();
    const value = line.slice(sep + 1).trim().replace(/^["']|["']$/g, '');
    if (key) data[key] = value;
  }

  return { data, body: raw.slice(match[0].length) };
}

function slugFromPath(path: string): string {
  // `split` rend toujours au moins un segment. L'affirmer au compilateur avec
  // `!` masquerait un vrai `undefined` le jour où l'appelant change.
  const segments = path.split('/');
  return (segments[segments.length - 1] || path).replace(/\.mdx?$/, '');
}

/** ~200 mots/minute, arrondi au supérieur, jamais moins d'une minute. */
function readingMinutes(body: string): number {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

/**
 * `cover:` est le champ que le builder de DispatchSEO remplit ; `image:` est
 * accepté aussi parce que c'est le nom le plus courant ailleurs. On ne garde
 * qu'un chemin servi par le site (`/…`) ou une URL https : n'importe quelle
 * autre forme finirait en `src` d'une balise `<img>` et en `og:image`.
 */
function coverFrom(data: Record<string, string>): string | undefined {
  const value = (data.cover || data.image || '').trim();
  return /^(\/[^/\s]|https:\/\/)\S*$/.test(value) ? value : undefined;
}

/**
 * La page rend déjà `title` en `<h1>`. Les articles remis par DispatchSEO
 * commencent pourtant par leur propre `# Titre` : sans ce retrait, la page a
 * deux titres de niveau 1, ce que Google comme les lecteurs d'écran lisent
 * mal. On ne retire qu'un `#` placé en tête du corps, jamais un `##`.
 */
function stripLeadingH1(body: string): string {
  return body.replace(/^\s*#[ \t]+[^\n]*\n+/, '');
}

function toArticle(path: string, raw: string): Article {
  const { data, body: rawBody } = parseFrontmatter(raw);
  const body = stripLeadingH1(rawBody);
  const slug = data.slug || slugFromPath(path);

  return {
    slug,
    title: data.title || slug,
    description: data.description || '',
    date: data.date || '',
    lang: isLang(data.lang) ? data.lang : DEFAULT_LANG,
    author: data.author || 'NeverZero',
    // `tags: a, b` comme `tags: [a, b]` (forme YAML de DispatchSEO).
    tags: (data.tags || '')
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((tag) => tag.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean),
    cover: coverFrom(data),
    markdown: body,
    readingMinutes: readingMinutes(body),
  };
}

/**
 * Un `draft: true` dans l'en-tête exclut l'article du site — c'est le seul
 * moyen de committer un brouillon sans le publier.
 */
const ARTICLES: Article[] = Object.entries(RAW_ARTICLES)
  .filter(([, raw]) => !/^---[\s\S]*?\bdraft:\s*true\b[\s\S]*?---/.test(raw))
  .map(([path, raw]) => toArticle(path, raw))
  .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.slug.localeCompare(b.slug)));

export function listArticles(lang?: Lang): Article[] {
  return lang ? ARTICLES.filter((article) => article.lang === lang) : ARTICLES;
}

export function findArticle(slug: string): Article | undefined {
  return ARTICLES.find((article) => article.slug === slug);
}

marked.setOptions({ gfm: true, breaks: false });

/**
 * Le Markdown vient de nos propres fichiers versionnés, jamais d'un visiteur :
 * il n'y a rien à assainir ici, et `marked` est appelé en mode synchrone pour
 * que le rendu SSR reste une simple fonction.
 */
export function renderMarkdown(markdown: string): string {
  return marked.parse(markdown, { async: false }) as string;
}

export function formatArticleDate(date: string, lang: Lang): string {
  if (!date) return '';
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
